import React, { useState, useEffect, useRef } from 'react';
import { Project, Status, ProjectsData } from '../types/projects';
import projectsData from '../data/projects.json';
import { WebIcon, ToolIcon, ApiIcon, GameIcon, MobileIcon } from '../icons';
import ProjectThumbnail from '../components/ProjectThumbnail';
import '../styles/Page.css';
import '../styles/Projects.css';

// Filter types
type FilterType = 'type' | 'relevance' | 'popularity';
type SortOrder = 'most-recent' | 'oldest' | 'alphabetical' | 'most-popular' | 'featured-first';

interface FilterState {
  type: string;
  relevance: SortOrder;
  popularity: SortOrder;
}

const Projects: React.FC = () => {
  const [data] = useState<ProjectsData>(projectsData as ProjectsData);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    type: 'all',
    relevance: 'most-recent',
    popularity: 'most-popular'
  });
  const [activeDropdown, setActiveDropdown] = useState<FilterType | null>(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const filterSystemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Trigger animations after component mounts
    setTimeout(() => setIsAnimated(true), 100);
  }, []);

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterSystemRef.current && !filterSystemRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    let projects = [...data.projects];

    // Filter by type
    if (filters.type !== 'all') {
      projects = projects.filter(project => 
        project.categories.includes(filters.type)
      );
    }

    // Sort by relevance
    switch (filters.relevance) {
      case 'most-recent':
        projects.sort((a, b) => new Date(b.release).getTime() - new Date(a.release).getTime());
        break;
      case 'oldest':
        projects.sort((a, b) => new Date(a.release).getTime() - new Date(b.release).getTime());
        break;
      case 'alphabetical':
        projects.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'featured-first':
        projects.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return new Date(b.release).getTime() - new Date(a.release).getTime();
        });
        break;
    }

    // Apply popularity sorting (secondary sort)
    if (filters.popularity === 'featured-first') {
      projects.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return 0;
      });
    }

    setFilteredProjects(projects);
  }, [filters, data.projects]);

  const getStatusConfig = (statusId: string): Status | undefined => {
    return data.statuses.find(status => status.id === statusId);
  };

  const getCategoryIcon = (categories: string[]) => {
    const iconProps = { size: 32, className: 'projects__category-icon' };
    
    // Use the first category for icon display
    const primaryCategory = categories[0] || 'tool';
    
    switch (primaryCategory) {
      case 'web':
        return <WebIcon {...iconProps} />;
      case 'tool':
        return <ToolIcon {...iconProps} />;
      case 'api':
        return <ApiIcon {...iconProps} />;
      case 'game':
        return <GameIcon {...iconProps} />;
      case 'mobile':
        return <MobileIcon {...iconProps} />;
      case 'AI':
        return <ToolIcon {...iconProps} />; // Use tool icon for AI for now
      default:
        return <ToolIcon {...iconProps} />;
    }
  };

  const handleFilterChange = (filterType: FilterType, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setActiveDropdown(null);
  };

  const toggleDropdown = (filterType: FilterType) => {
    setActiveDropdown(activeDropdown === filterType ? null : filterType);
  };

  // Filter options
  const typeOptions = data.categories.map(cat => ({ value: cat.id, label: cat.name }));
  const relevanceOptions = [
    { value: 'most-recent', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'alphabetical', label: 'Alphabetical' },
    { value: 'featured-first', label: 'Featured First' }
  ];
  const popularityOptions = [
    { value: 'most-popular', label: 'Most Popular' },
    { value: 'featured-first', label: 'Featured Projects' },
    { value: 'most-recent', label: 'Recently Updated' }
  ];

  const getFilterLabel = (filterType: FilterType): string => {
    switch (filterType) {
      case 'type':
        return typeOptions.find(opt => opt.value === filters.type)?.label || 'All Projects';
      case 'relevance':
        return relevanceOptions.find(opt => opt.value === filters.relevance)?.label || 'Most Recent';
      case 'popularity':
        return popularityOptions.find(opt => opt.value === filters.popularity)?.label || 'Most Popular';
    }
  };

  const getFilterOptions = (filterType: FilterType) => {
    switch (filterType) {
      case 'type':
        return typeOptions;
      case 'relevance':
        return relevanceOptions;
      case 'popularity':
        return popularityOptions;
    }
  };

  return (
    <div className="page">
      <div className="page__container">
        {/* Simple Title */}
        <h1 className="projects__simple-title">Open Source</h1>
        
        {/* Enhanced Filter System */}
        <div className={`projects__filter-system ${isAnimated ? 'projects--animated' : ''}`} ref={filterSystemRef}>
          <div className="projects__filter-header">
            <h3 className="projects__filter-title">Filter & Sort</h3>
            <span className="projects__filter-count">{filteredProjects.length} projects found</span>
          </div>
          
          <div className="projects__filters-container">
            {/* Project Type Filter */}
            <div className="projects__filter-group">
              <label className="projects__filter-label">Project Type</label>
              <div className="projects__filter-dropdown">
                <button
                  className={`projects__filter-button ${activeDropdown === 'type' ? 'projects__filter-button--active' : ''}`}
                  onClick={() => toggleDropdown('type')}
                  aria-expanded={activeDropdown === 'type'}
                  aria-haspopup="listbox"
                  aria-label="Filter by project type"
                >
                  <span className="projects__filter-button-text">{getFilterLabel('type')}</span>
                  <svg 
                    className={`projects__filter-button-icon ${activeDropdown === 'type' ? 'projects__filter-button-icon--rotated' : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>
                <div 
                  className={`projects__filter-options ${activeDropdown === 'type' ? 'projects__filter-options--visible' : ''}`}
                  role="listbox"
                  aria-label="Project type options"
                >
                  {getFilterOptions('type').map((option) => (
                    <button
                      key={option.value}
                      className={`projects__filter-option ${filters.type === option.value ? 'projects__filter-option--selected' : ''}`}
                      onClick={() => handleFilterChange('type', option.value)}
                      role="option"
                      aria-selected={filters.type === option.value}
                    >
                      {option.label}
                      {filters.type === option.value && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Relevance Filter */}
            <div className="projects__filter-group">
              <label className="projects__filter-label">Sort By</label>
              <div className="projects__filter-dropdown">
                <button
                  className={`projects__filter-button ${activeDropdown === 'relevance' ? 'projects__filter-button--active' : ''}`}
                  onClick={() => toggleDropdown('relevance')}
                  aria-expanded={activeDropdown === 'relevance'}
                  aria-haspopup="listbox"
                  aria-label="Sort projects by relevance"
                >
                  <span className="projects__filter-button-text">{getFilterLabel('relevance')}</span>
                  <svg 
                    className={`projects__filter-button-icon ${activeDropdown === 'relevance' ? 'projects__filter-button-icon--rotated' : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>
                <div 
                  className={`projects__filter-options ${activeDropdown === 'relevance' ? 'projects__filter-options--visible' : ''}`}
                  role="listbox"
                  aria-label="Project sorting options"
                >
                  {getFilterOptions('relevance').map((option) => (
                    <button
                      key={option.value}
                      className={`projects__filter-option ${filters.relevance === option.value ? 'projects__filter-option--selected' : ''}`}
                      onClick={() => handleFilterChange('relevance', option.value)}
                      role="option"
                      aria-selected={filters.relevance === option.value}
                    >
                      {option.label}
                      {filters.relevance === option.value && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Popularity Filter */}
            <div className="projects__filter-group">
              <label className="projects__filter-label">Priority</label>
              <div className="projects__filter-dropdown">
                <button
                  className={`projects__filter-button ${activeDropdown === 'popularity' ? 'projects__filter-button--active' : ''}`}
                  onClick={() => toggleDropdown('popularity')}
                  aria-expanded={activeDropdown === 'popularity'}
                  aria-haspopup="listbox"
                  aria-label="Sort projects by priority"
                >
                  <span className="projects__filter-button-text">{getFilterLabel('popularity')}</span>
                  <svg 
                    className={`projects__filter-button-icon ${activeDropdown === 'popularity' ? 'projects__filter-button-icon--rotated' : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>
                <div 
                  className={`projects__filter-options ${activeDropdown === 'popularity' ? 'projects__filter-options--visible' : ''}`}
                  role="listbox"
                  aria-label="Project priority options"
                >
                  {getFilterOptions('popularity').map((option) => (
                    <button
                      key={option.value}
                      className={`projects__filter-option ${filters.popularity === option.value ? 'projects__filter-option--selected' : ''}`}
                      onClick={() => handleFilterChange('popularity', option.value)}
                      role="option"
                      aria-selected={filters.popularity === option.value}
                    >
                      {option.label}
                      {filters.popularity === option.value && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className={`projects__grid ${isAnimated ? 'projects--animated' : ''}`}>
          {filteredProjects.map((project, index) => {
            const statusConfig = getStatusConfig(project.status);
            return (
              <div 
                key={project.id} 
                className="projects__card"
                style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
              >
                <div className="projects__card-header">
                  {/* Removed icon container and status meta */}
                </div>
                
                {/* Project Thumbnail */}
                <ProjectThumbnail
                  projectId={project.id}
                  emojiThumbnail={project.emoji}
                  alt={`${project.title} thumbnail`}
                  className=""
                />
                
                <div className="projects__card-content">
                  <h3 className="projects__card-title">{project.title}</h3>
                  <p className="projects__card-description">{project.description}</p>
                  
                  <div className="projects__technologies">
                    {project.technologies.slice(0, 3).map((tech, techIndex) => (
                      <span key={techIndex} className="projects__tech">
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="projects__tech projects__tech--more">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                <div className="projects__card-footer">
                  <div className="projects__actions">
                    {project.githubUrl && (
                      <a 
                        href={project.githubUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--github"
                        title="View on GitHub"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </a>
                    )}
                    {project.pypiUrl && (
                      <a 
                        href={project.pypiUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--pypi"
                        title="View on PyPI"
                      >
                        <span className="projects__action-text">PyPI</span>
                      </a>
                    )}
                    {project.cratesUrl && (
                      <a 
                        href={project.cratesUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--crates"
                        title="View on Crates.io"
                      >
                        <svg width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
                          <path d="M508.52 249.75l-21.25-11.5c-1.25-12.75-3.75-25.25-7.25-37.5l15-18.75c8.25-10.25 7.75-25-.75-34.75l-24.5-28.25c-8.5-9.75-22.75-12-34.25-5.5l-21 11.75c-9.5-8.75-19.75-16.75-30.75-23.75l2.25-23.5c1.25-13-7.5-24.75-20.25-27.25l-36.75-7.25c-12.75-2.5-25.5 4.5-29.5 16.75l-7.25 22.25c-13-1.75-26.25-2.25-39.5-1.5l-11.75-21c-6.5-11.5-20.75-16.5-32.75-11.5l-34.5 14.25c-12 5-18.25 18.75-14.25 31.5l7.25 23c-10.75 6.75-20.75 14.5-30 23.25l-22.5-6.75c-12.5-3.75-26 2.25-31 14l-14.5 34.25c-5 11.75.5 25.5 12.5 31.75l21.75 11.25c1.5 12.75 4 25.25 7.5 37.5l-15 18.75c-8.25 10.25-7.75 25 .75 34.75l24.5 28.25c8.5 9.75 22.75 12 34.25 5.5l21-11.75c9.5 8.75 19.75 16.75 30.75 23.75l-2.25 23.5c-1.25 13 7.5 24.75 20.25 27.25l36.75 7.25c12.75 2.5 25.5-4.5 29.5-16.75l7.25-22.25c13 1.75 26.25 2.25 39.5 1.5l11.75 21c6.5 11.5 20.75 16.5 32.75 11.5l34.5-14.25c12-5 18.25-18.75 14.25-31.5l-7.25-23c10.75-6.75 20.75-14.5 30-23.25l22.5 6.75c12.5 3.75 26-2.25 31-14l14.5-34.25c5-11.75-.5-25.5-12.5-31.75zM256 344c-48.5 0-88-39.5-88-88s39.5-88 88-88 88 39.5 88 88-39.5 88-88 88z"/>
                        </svg>
                      </a>
                    )}
                    {project.npmUrl && (
                      <a 
                        href={project.npmUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--npm"
                        title="View on npm"
                      >
                        <span className="projects__action-text">npm</span>
                      </a>
                    )}
                    {project.vscodeUrl && (
                      <a 
                        href={project.vscodeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--vscode"
                        title="View on VS Code Marketplace"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
                        </svg>
                      </a>
                    )}
                    {project.docsUrl && (
                      <a 
                        href={project.docsUrl} 
                        className="projects__action projects__action--docs"
                        title="View Documentation"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14,2 14,8 20,8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10,9 9,9 8,9"/>
                        </svg>
                      </a>
                    )}
                    {project.liveUrl && (
                      <a 
                        href={project.liveUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="projects__action projects__action--live"
                        title="View Live Site"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15,3 21,3 21,9"/>
                          <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                    )}
                  </div>
                  
                  {project.version && (
                    <div className="project__updated">
                      {project.version}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="projects__empty">
            <p>No projects found with the current filters.</p>
          </div>
        )}

        {/* Terminal Section */}
        <div className={`page__terminal ${isAnimated ? 'projects--animated' : ''}`}>
          <div className="terminal__line">
            <span className="terminal__prompt">griffin@matrix:~/projects$</span>
            <span className="terminal__command">git status</span>
          </div>
          <div className="terminal__output">
            <div>On branch main</div>
            <div>Your branch is up to date with 'origin/main'.</div>
            <div>Projects loaded: {data.projects.length}</div>
            <div>Categories available: {data.categories.length - 1}</div>
            <div>Nothing to commit, working tree clean</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`projects__cta ${isAnimated ? 'projects--animated' : ''}`}>
          <p>Want to collaborate or have questions about any project?</p>
          <a href="mailto:griffin@example.com" className="btn btn--primary">
            Get In Touch
          </a>
        </div>
      </div>
    </div>
  );
};

export default Projects; 